import { format } from "date-fns";
import React from "react";
import { CheckIcon } from "@heroicons/react/24/solid";

export interface ProgressStep {
  title: string;
  description: string | React.ReactNode;
  state: "completed" | "current" | "upcoming";
  completedDate?: Date;
}

interface CaseProgressListProps {
  steps: ProgressStep[];
}

const CaseProgressList: React.FC<CaseProgressListProps> = ({ steps }) => {
  return (
    <div className="flex flex-col">
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;

        // The connector below a node reflects whether that node itself is
        // behind us (evergreen) or still ahead (oat-200), independent of the
        // node's own circle treatment.
        const connectorColor =
          step.state === "completed" ? "bg-evergreen" : "bg-oat-200";
        const textColor =
          step.state === "upcoming" ? "text-oat-700" : "text-soil";

        return (
          <div
            key={index}
            data-active={step.state}
            className="relative flex items-start gap-3"
          >
            <div className="flex flex-col items-center">
              {step.state === "completed" && (
                <div
                  data-state={step.state}
                  className="relative z-10 flex h-7 w-7 items-center justify-center rounded-full border border-evergreen bg-evergreen"
                >
                  <CheckIcon
                    className="h-4 w-4 text-ivory"
                    aria-hidden="true"
                  />
                </div>
              )}
              {step.state === "current" && (
                <div
                  data-state={step.state}
                  className="relative z-10 flex h-7 w-7 animate-soft-pulse items-center justify-center rounded-full border border-cantelope bg-cantelope"
                >
                  <div className="h-2 w-2 rounded-full bg-soil" />
                </div>
              )}
              {step.state === "upcoming" && (
                <div
                  data-state={step.state}
                  className="relative z-10 h-7 w-7 rounded-full border-2 border-oat-200 bg-white"
                />
              )}

              {!isLast && (
                <div className="absolute z-0 h-full grow">
                  <div
                    className={`mx-auto mt-1 h-full w-0.5 ${connectorColor}`}
                  ></div>
                </div>
              )}
            </div>

            <div className={`flex w-full flex-col gap-3 pb-8 ${textColor}`}>
              <div className="flex flex-col gap-1">
                <div className="text-lg font-bold leading-tight">
                  {step.title}
                </div>
                {step.state === "completed" && step.completedDate && (
                  <p className="text-xs font-bold text-evergreen">
                    Completed on {format(step.completedDate, "MMMM d, yyyy")}
                  </p>
                )}
              </div>
              <div className="text-base leading-normal">{step.description}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const CaseProgressStepSkeleton: React.FC<{
  title: string;
  isLast?: boolean;
}> = ({ title, isLast = false }) => {
  return (
    <div className="relative flex items-start gap-3">
      <div className="flex flex-col items-center">
        <div className="relative z-10 h-7 w-7 rounded-full bg-gray-200"></div>
        {!isLast && (
          <div className="absolute z-0 h-full grow">
            <div className="mx-auto mt-1 h-full w-0.5 bg-gray-200"></div>
          </div>
        )}
      </div>

      <div className="flex w-full flex-col gap-3 pb-8">
        <div className="flex flex-col gap-1">
          <div className="text-lg font-bold leading-tight text-soil">
            {title}
          </div>
        </div>
        <div className="space-y-2">
          <div className="h-4 w-full rounded bg-gray-200"></div>
          <div className="h-4 w-3/4 rounded bg-gray-200"></div>
        </div>
      </div>
    </div>
  );
};

export const CaseProgressListSkeleton: React.FC<{
  steps: Array<{ title: string }>;
}> = ({ steps }) => {
  return (
    <div className="flex flex-col">
      {steps.map((step, index) => (
        <CaseProgressStepSkeleton
          key={index}
          title={step.title}
          isLast={index === steps.length - 1}
        />
      ))}
    </div>
  );
};

export default CaseProgressList;
